// Shared PDF palette + typography for usinage report exporters.
//
// All reports use the same scale + grey palette so the rapport and
// the couplage matrix read as documents from the same document
// family. Pulled into a sibling module so the per-report exporters
// stay focused on layout.

// Neutral grey palette only — matches the FE design system tokens.
export const PDF_BORDER: [number, number, number] = [209, 213, 219] // gray-300
export const PDF_SECTION_BAND: [number, number, number] = [243, 244, 246] // gray-100
export const PDF_HEADER_BAND: [number, number, number] = [249, 250, 251] // gray-50
export const PDF_TOTAL_BAND: [number, number, number] = [229, 231, 235] // gray-200

export const PDF_TEXT_PRIMARY = 25   // near-black, gray-900
export const PDF_TEXT_SECONDARY = 75 // gray-600
export const PDF_TEXT_MUTED = 130    // gray-500

// Type scale — picked for a finance / lab report feel. Helvetica
// (jsPDF built-in) so no font-embedding bundle cost.
export const PDF_TYPE = {
  title: { size: 15, weight: "bold" as const },
  meta: { size: 9, weight: "normal" as const },
  sectionHeader: { size: 10, weight: "bold" as const },
  tableHeader: { size: 8.5, weight: "bold" as const },
  tableBody: { size: 9, weight: "normal" as const },
  freinteLabel: { size: 8, weight: "bold" as const },
  freinteValue: { size: 13, weight: "bold" as const },
  footer: { size: 8, weight: "normal" as const },
}

export function pdfTableStyles() {
  return {
    fontSize: PDF_TYPE.tableBody.size,
    cellPadding: 6,
    lineColor: PDF_BORDER,
    lineWidth: 0.5,
    textColor: PDF_TEXT_PRIMARY,
    font: "helvetica" as const,
  }
}

export function pdfHeadStyles() {
  return {
    fillColor: PDF_HEADER_BAND,
    textColor: PDF_TEXT_PRIMARY,
    fontStyle: PDF_TYPE.tableHeader.weight,
    fontSize: PDF_TYPE.tableHeader.size,
  }
}

// Locale used for every number rendered into PDF tables. fr-FR so the
// document reads naturally for operators (decimal comma, grouped
// thousands), independent of the user's current i18n language.
const PDF_NUMBER_LOCALE = "fr-FR"

// formatPdfNumber renders a number into a string safe for jsPDF's
// built-in WinAnsi-encoded Helvetica. Intl.NumberFormat for fr-FR
// emits U+202F (NARROW NO-BREAK SPACE) as its thousands separator,
// which the built-in font has no glyph for — that's what shows up as
// a stray "/"-looking mark in printed reports (operator reported
// "30/50" instead of "30,50" and similar). We also normalise the
// regular non-breaking space (U+00A0) that some browsers emit
// instead. Both get replaced with an ordinary ASCII space so the
// thousands separator stays visually correct.
export function formatPdfNumber(
  value: number | null | undefined,
  fractionDigits = 0,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "0"
  const raw = new Intl.NumberFormat(PDF_NUMBER_LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)
  return raw.replace(/[  ]/g, " ")
}
